import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Ban, RotateCcw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api from "../../lib/api";

export const forgetVendor = async (inv) => {
  const name = inv.vendor || inv.email?.from || "this vendor";
  if (!window.confirm(`Forget ${name}? This deletes every invoice from them and blocks future ones from coming in.`)) return false;
  try {
    const { data } = await api.post(`/ap/invoices/${inv.id}/forget-vendor`);
    toast.success(`${data.name} forgotten`, { description: `${data.removed} invoice${data.removed === 1 ? "" : "s"} removed` });
    return true;
  } catch (e) { toast.error("Could not forget vendor", { description: e.response?.data?.detail }); return false; }
};

export function ApForgotten({ refreshKey, onChanged }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const load = () => api.get("/ap/forgotten-vendors").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]);
  const restore = async (v) => {
    await api.delete(`/ap/forgotten-vendors/${v.id}`);
    toast.success(`${v.name} restored`, { description: "Future invoices from them will come in again." });
    load(); onChanged();
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="ap-forgotten-btn"
        className="inline-flex items-center gap-1.5 text-sm text-brand-muted hover:text-brand-ink transition-colors">
        <Ban className="h-3.5 w-3.5" /> Forgotten vendors ({items.length})
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-brand-page border-brand-border" data-testid="ap-forgotten-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl font-light text-brand-ink">Forgotten vendors</DialogTitle>
            <DialogDescription className="text-xs text-brand-muted">Invoices from these vendors are ignored. Restore lets new ones in again; deleted invoices stay deleted.</DialogDescription>
          </DialogHeader>
          {!items.length ? <p className="text-sm text-brand-muted py-6 text-center" data-testid="ap-forgotten-empty">No forgotten vendors.</p> : (
            <ul className="divide-y divide-brand-border max-h-[420px] overflow-y-auto">
              {items.map((v) => (
                <li key={v.id} className="flex items-center gap-3 py-3" data-testid={`ap-forgotten-${v.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-brand-ink font-medium truncate">{v.name}</p>
                    <p className="text-xs text-brand-muted truncate">{[v.email, `by ${v.by}`, new Date(v.at).toLocaleDateString()].filter(Boolean).join(" · ")}</p>
                  </div>
                  <button onClick={() => restore(v)} data-testid={`ap-restore-${v.id}`}
                    className="inline-flex items-center gap-1 border border-brand-border rounded-full px-3 py-1 text-xs text-brand-ink hover:bg-brand-hover transition-colors">
                    <RotateCcw className="h-3 w-3" /> Restore
                  </button>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
